import { GraphQLClient } from 'graphql-request';
import type { IncomingHttpHeaders } from 'http';
import Cookies from 'js-cookie';

import { ATHENA_URL, DEPLOY_ENV, RAILS_URL, XSRF_COOKIE_KEY } from '../globals';

interface fetchWithRetryOptions extends RequestInit {
  retries?: number;
  retryDelay?: number; // in milliseconds
}

/**
 * A wrapper around `fetch` with optional retries.
 * Retries up to `options.retries` times (defaults to `5` if not specified)
 * with a delay of `options.retryDelay` (defaults to `500ms` if not specified)
 */
const fetchWithRetry = async (url: string, options: fetchWithRetryOptions): Promise<Response> => {
  const retries = options.retries || 0;
  const retryDelay = options.retryDelay || 500;

  try {
    return await fetch(url, options);
  } catch (err) {
    if (retries < 1) throw err;

    // sleep the specified amount of time, then recursively retry
    await new Promise((r) => setTimeout(r, retryDelay));
    return await fetchWithRetry(url, { ...options, retries: retries - 1 });
  }
};

/**
 * Merges caller-supplied headers over the JSON and CSRF defaults.
 *
 * Goes through `Headers` rather than an object spread because `HeadersInit` also admits a
 * `Headers` and a `string[][]`; spreading either yields `{}` or index keys, which would drop
 * the CSRF token along with the caller's own headers.
 */
const withDefaultHeaders = (callerHeaders: HeadersInit | undefined): Headers => {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-CSRF-Token': Cookies.get(XSRF_COOKIE_KEY) || '',
  });
  new Headers(callerHeaders).forEach((value, name) => {
    headers.set(name, value);
  });
  return headers;
};

// To do: strengthen jsonParam's type
// Do not pass skip doBFFPost as it will not actually skip making the request - instead handle it conditionally
export function doBFFPost(
  apiUrl: string,
  jsonParams: object,
  options: Omit<fetchWithRetryOptions, 'method'> = {},
): Promise<Response> {
  return fetchWithRetry(apiUrl, {
    retries: 5,
    retryDelay: 500,
    credentials: 'include',
    method: 'POST',
    body: JSON.stringify(jsonParams),
    ...options,
    headers: withDefaultHeaders(options.headers),
  });
}

export function getAthenaClient(
  incomingRequestHeaders?: IncomingHttpHeaders,
  options?: { signal?: AbortSignal },
) {
  // Use explicit headers if provided, otherwise try AsyncLocalStorage context
  // This allows SSR pages to wrap their logic with withRequestContext() and have
  // all nested getAthenaClient() calls automatically receive headers
  let effectiveHeaders = incomingRequestHeaders;

  // Only import requestContext on server-side to prevent webpack bundling async_hooks for client
  if (!effectiveHeaders && typeof window === 'undefined') {
    const { getRequestHeaders } = require('./requestContext');
    effectiveHeaders = getRequestHeaders();
  }

  let tivoHeaders: any = [];
  if (DEPLOY_ENV === 'test') {
    if (effectiveHeaders) {
      // Filter out any headers that are not prefixed with 'x-tivo-'
      tivoHeaders = Object.fromEntries(
        Object.entries(effectiveHeaders).filter(([key]) => key.startsWith('x-tivo-')),
      );
    }
  }

  // Forward cookies for SSR authentication
  const headers: Record<string, string> = {
    'apollographql-client-name': 'cosmos',
    ...tivoHeaders,
  };

  // If we have request headers (explicit or from context), forward the cookie header
  if (effectiveHeaders?.cookie) {
    headers.cookie = effectiveHeaders.cookie;
  }

  // Local development auth bypass - send user token to local Athena
  const devUserToken =
    process.env.ATHENA_DEV_USER_TOKEN || process.env.NEXT_PUBLIC_ATHENA_DEV_USER_TOKEN;
  if (DEPLOY_ENV === 'development' && devUserToken) {
    headers['x-athena-request-user'] = devUserToken;
  }

  // Return the graphql-request client directly; call sites invoke
  // `.request(SomeDocument, variables)` with a generated TypedDocumentNode,
  // which infers result/variable types without a generated SDK.
  return new GraphQLClient(ATHENA_URL, {
    credentials: 'include',
    headers,
    // graphql-request vendors its own DOM type defs whose AbortSignal
    // diverges from the native type. Identical at runtime.
    signal: options?.signal ?? undefined,
  });
}

export function doGet(
  path: string,
  options: Omit<fetchWithRetryOptions, 'body' | 'method'> = {},
): Promise<Response> {
  return fetchWithRetry(`${RAILS_URL}${path}`, {
    retries: 5,
    retryDelay: 500,
    credentials: 'include',
    ...options,
    headers: withDefaultHeaders(options.headers),
  });
}
